/**
=========================================================
* E4A-IoT-Manager React Frontend Dashboard 
* Copyright © 2024 Electronics4All Inc.
=========================================================
*/

import { useState, useEffect } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import Grid from "@mui/material/Grid";

// Material Dashboard React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

function PermissionManagement({
  initialPermissions,
  setPermissionList
}) {
  const [permissions, setPermissions] = useState({});

  const defaultPermissionId = "68d59753ab16d12e9a91819e";

  useEffect(() => {
    if (Array.isArray(initialPermissions)) {
      const hasDefault = initialPermissions.includes(defaultPermissionId);
      const ensuredPermissions = hasDefault
        ? initialPermissions
        : [...initialPermissions, defaultPermissionId];

      const perms = ensuredPermissions.reduce((acc, id) => {
        acc[id] = true;
        return acc;
      }, {});
      setPermissions(perms);

      if (!hasDefault) {
        setPermissionList(prev =>
          prev.includes(defaultPermissionId) ? prev : [...prev, defaultPermissionId]
        );
      }
    } else {
      setPermissions({ [defaultPermissionId]: true });
      setPermissionList([defaultPermissionId]);
    }
  }, [initialPermissions, setPermissionList]);

  const addToPermissionList = (id) => {
    setPermissionList(prev => {
      if (prev.includes(id)) {
        return prev.filter(pid => pid !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const removeFromPermissionList = (id) => {
    setPermissionList(prev => prev.filter(pid => pid !== id));
  };

  const permissionChangeHandler = (id) => {
    setPermissions(prev => ({
      ...prev,
      [id]: !prev[id],
    }));

    if (permissions[id]) {
      removeFromPermissionList(id);
    } else {
      addToPermissionList(id);
    }
  };

  const PermissionRow = ({ fullText, id, disableComponent = false }) => {
    return (
      <MDBox display="flex" alignItems="center" mb={0.5} ml={-1.5}>
        <MDBox mt={0.5}>
          <Switch checked={Boolean(permissions[id])} onChange={() => permissionChangeHandler(id)} disabled={disableComponent} />
        </MDBox>
        <MDBox width="80%" ml={0.5}>
          <MDTypography variant="button" fontWeight="regular" color="text">
            {fullText}
          </MDTypography>
        </MDBox>
      </MDBox>
    );
  };

  return (
    <Card sx={{ boxShadow: "none" }}>
      <MDBox pt={1} pb={2} px={2} lineHeight={1.25}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>

            {/* View Entire Sections (navBar sections) */}
            <MDBox>
              <MDTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
                Sections
              </MDTypography>
            </MDBox>

            <PermissionRow fullText="View User Profile" id="68d59753ab16d12e9a91819e" disableComponent={true} />
            <PermissionRow fullText="View Dashboards Section" id="68d599f0ab16d12e9a9181a0" />
            <PermissionRow fullText="View Part Code Management Section" id="68d59753ab16d12e9a91819b" />
            <PermissionRow fullText="View Inventory Management Section" id="68d59753ab16d12e9a91819c" />
            <PermissionRow fullText="View Location Management Section" id="68e3c9bb0fd2e700878f3afa" />
            <PermissionRow fullText="View Fulfillment Center Section" id="68eff6f0df403639ba35c93e" />
            <PermissionRow fullText="View User Management Section" id="68d59753ab16d12e9a91819d" />
            <PermissionRow fullText="View Change Logs Section" id="6920ba9eed0538f745f928d6" />

            {/* Part Codes */}
            <MDBox>
              <MDTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
                Part Codes
              </MDTypography>
            </MDBox>

            <PermissionRow fullText="Create Part Codes" id="67ebf5bb5c8092d6a2e4ab29" />
            <PermissionRow fullText="Edit Part Codes" id="67ebf5bb5c8092d6a2e4ab2a" />
            <PermissionRow fullText="Delete Part Codes" id="67ebf5bb5c8092d6a2e4ab25" />
            <PermissionRow fullText="Delete Approved Part Codes" id="68d5a0a5ab16d12e9a9181a4" />
            <PermissionRow fullText="Approve/Reject Part Codes" id="67ebf5bb5c8092d6a2e4ab2b" />
            <PermissionRow fullText="Modify Part Codes BOM" id="67ebf5bb5c8092d6a2e4ab28" />
            <PermissionRow fullText="Mark as Obsolete" id="692efb078dd17074cad0d2cb" />

            {/* Inventory Items */}
            <MDBox mt={1}>
              <MDTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
                Inventory Items
              </MDTypography>
            </MDBox>

            <PermissionRow fullText="Create Inventory Items" id="67ebf5bb5c8092d6a2e4ab2f" />
            <PermissionRow fullText="Edit Inventory Items" id="67ebf5bb5c8092d6a2e4ab30" />
            <PermissionRow fullText="Delete Inventory Items" id="67ebf5bb5c8092d6a2e4ab2e" />
            <PermissionRow fullText="Adjust Inventory Items Stock levels" id="680932f10b6ec66cf087f263" />
            <PermissionRow fullText="Create Reservation" id="6931c05bc804399c6791c1ab"/>
            <PermissionRow fullText="Manage Reservations" id="6931c070c804399c6791c1ad"/>
          </Grid>

          <Grid item xs={12} md={6}>
            {/* Part Categories */}
            <MDBox mt={1}>
              <MDTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
                Dashboards
              </MDTypography>
            </MDBox>

            <PermissionRow fullText="View E4A Dashboard" id="693adcfcfda146cf3bc4f684" />
            <PermissionRow fullText="View Clients Dashboard" id="693adcfcfda146cf3bc4f685" />
            {/* Part Categories */}
            <MDBox mt={1}>
              <MDTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
                Part Categories
              </MDTypography>
            </MDBox>

            <PermissionRow fullText="Create Part Categories" id="67ebf5bb5c8092d6a2e4ab21" />
            <PermissionRow fullText="Edit Part Categories" id="67ebf5bb5c8092d6a2e4ab22" />
            {/* <PermissionRow fullText="Delete Part Categories" id="67ebf5bb5c8092d6a2e4ab20" /> */}

            {/* Location Management */}
            <MDBox mt={1}>
              <MDTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
                Location Management
              </MDTypography>
            </MDBox>

            <PermissionRow fullText="Add Location" id="68e3d8ca0fd2e700878f3afd" />
            <PermissionRow fullText="Edit Location" id="68e7da7f627cb171b1151973" />

            {/* Fulfillment Center */}
            <MDBox mt={1}>
              <MDTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
                Fulfillment Center
              </MDTypography>
            </MDBox>

            <PermissionRow fullText="Add Product" id="68eff6f0df403639ba35c93f" />
            <PermissionRow fullText="Edit Product" id="68f8f414642f336d77d15cf4" />
            <PermissionRow fullText="View Product History" id="692751d482eb7530e1d4cd8c"/>
            <PermissionRow fullText="Delete Product" id="6942fdaece2b9bda84c39e04"/>

            {/* Orders */}
            <MDBox mt={1}>
              <MDTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
                Orders
              </MDTypography>
            </MDBox>

            <PermissionRow fullText="Manage Orders" id="691233345a7a6816ecc7b11c" />
            <PermissionRow fullText="Create Orders" id="691233345a7a6816ecc7b11d" />
            <PermissionRow fullText="Edit Orders" id="691233345a7a6816ecc7b11e" />
            <PermissionRow fullText="View Order History" id="692751d482eb7530e1d4cd8d"/>
            <PermissionRow fullText="Delete Orders" id="6942fdaece2b9bda84c39e05"/>

            {/* Organizations */}
            <MDBox mt={1}>
              <MDTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
                Organizations
              </MDTypography>
            </MDBox>

            <PermissionRow fullText="Manage Organizations" id="68f2a8c0f7fa18a6764d40a6" />
            <PermissionRow fullText="Create Organizations" id="68f29819f7fa18a6764d409b" />
            <PermissionRow fullText="Edit Organizations" id="68f29819f7fa18a6764d409c" />
            {/* <PermissionRow fullText="Delete Organizations" id="68f29819f7fa18a6764d409d" /> */}

            {/* Dispatches */}
            <MDBox mt={1}>
              <MDTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
                Dispatches
              </MDTypography>
            </MDBox>

            <PermissionRow fullText="Request Dispatch" id="691233345a7a6816ecc7b121" />
            <PermissionRow fullText="Manage Dispatches" id="691233345a7a6816ecc7b120" />
            <PermissionRow fullText="Delete Dispatches" id="6942fdaece2b9bda84c39e06"/>

          </Grid>
        </Grid>
      </MDBox>
    </Card>
  );
}

export default PermissionManagement;
